import React, { useState, useEffect } from 'react'
import { TrendingUp, TrendingDown, Package, CircleX, AlertTriangle, DollarSign, ShoppingCart, ArrowUpDown, HandCoins, Wallet } from 'lucide-react'
import { useTranslation } from '../LanguageContext'

export default function StatsMonths({ data, financial, loading }) {
  const { t } = useTranslation()
  const [stats, setStats] = useState([])

  useEffect(() => {
    if (data) {
      setStats([
        {
          label: t('stats.total_products'),
          value: data?.total_products || 0,
          change: "+0%",
          positive: true,
          icon: <Package className="w-5 h-5" />
        },
        {
          label: t('stats.low_stock_items'),
          value: data?.low_stock_products?.toString() || "0",
          change: t('stats.monitor'),
          positive: data?.low_stock_products === 0,
          icon: <AlertTriangle className="w-5 h-5" />
        },
        {
          label: t('stats.out_of_stock'),
          value: data?.out_of_stock?.toString() || "0",
          change: "+0%",
          positive: true,
          icon: <CircleX className="w-5 h-5" />
        },
        {
          label: t('stats.stock_value'),
          value: `${data?.total_value?.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }) || "0.00"} MAD`,
          change: "+0%",
          positive: true,
          icon: <DollarSign className="w-5 h-5" />
        },
      ])
    }
  }, [data, t])

  const financialCards = financial ? [
    {
      label: t('dashboard.total_sales'),
      value: `${Number(financial.total_sales).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MAD`,
      icon: <ShoppingCart className="w-5 h-5" />,
      color: 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400',
    },
    {
      label: t('dashboard.total_payments'),
      value: `${Number(financial.total_payments).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MAD`,
      icon: <ArrowUpDown className="w-5 h-5" />,
      color: 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400',
    },
    {
      label: t('dashboard.total_charges'),
      value: `${Number(financial.total_charges).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MAD`,
      icon: <HandCoins className="w-5 h-5" />,
      color: 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400',
    },
    {
      label: t('dashboard.profit'),
      value: `${Number(financial.profit).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MAD`,
      icon: <Wallet className="w-5 h-5" />,
      color: Number(financial.profit) >= 0 ? 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400' : 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400',
    },
    {
      label: t('dashboard.profit_exact'),
      value: `${Number(financial.profit_exact).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MAD`,
      icon: <Wallet className="w-5 h-5" />,
      color: Number(financial.profit_exact) >= 0 ? 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400' : 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400',
    },
  ] : []

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg px-4 py-5">
              <div className="animate-pulse">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-4"></div>
                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
              </div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg px-4 py-5">
              <div className="animate-pulse">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-4"></div>
                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-4">
        {stats.map((stat, idx) => (
          <div key={idx} className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg px-4 py-5">
            <div className="flex items-start justify-between">
              <div className="w-full flex items-start gap-3">
                <div className={`p-2 rounded-lg ${
                  stat.positive
                    ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
                    : 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400'
                }`}>
                  {stat.icon}
                </div>
                <div className='w-60'>
                  <div className='flex items-center justify-between w-full'>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{stat.label}</p>
                    <span
                      className={`inline-flex items-center text-[11px] font-medium px-2 pt-0.5 rounded-full ${
                        stat.positive
                          ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                          : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                      }`}
                    >
                      {stat.positive && stat.change !== "Monitor" ? (
                        <TrendingUp className="w-2.5 h-2.5 mr-1" />
                      ) : stat.change === "Monitor" ? (
                        <AlertTriangle className="w-2.5 h-2.5 mr-1" />
                      ) : (
                        <TrendingDown className="w-2.5 h-2.5 mr-1" />
                      )}
                      {stat.change}
                    </span>
                  </div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-50 mt-2">{stat.value}</p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {financialCards.length > 0 && (
        <>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-50">{t('dashboard.financial_title')}</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-5 gap-4">
            {financialCards.map((card, idx) => (
              <div key={idx} className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg px-4 py-5">
                <div className="flex items-start justify-between">
                  <div className="w-full flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${card.color}`}>
                      {card.icon}
                    </div>
                    <div className='w-60'>
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{card.label}</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-gray-50 mt-2">{card.value}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
